/**
 * Calculates the appropriate text color (black or white) based on the contrast with the given background color.
 *
 * @param {string} hexColor - The hexadecimal representation of the background color.
 * @returns {string} The hexadecimal representation of the recommended text color.
 */
export function getContrastColor(hexColor) {
  // Convert hex to RGB
  const hex = hexColor.replace('#', '');
  const r = Number.parseInt(hex.substring(0, 2), 16);
  const g = Number.parseInt(hex.substring(2, 4), 16);
  const b = Number.parseInt(hex.substring(4, 6), 16);

  // Calculate the relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5 ? '#000' : '#fff';
}

/**
 * Highlights occurrences of a specified term within a given text.
 *
 * This function searches the input `text` for all occurrences of `term`
 * (case-insensitive) and wraps each occurrence in a `<span>` with the
 * class `highlighted-text`. This allows custom styling to emphasize
 * the matched term in rendered HTML.
 *
 * @param {string} text - The full text where the term will be highlighted.
 * @param {string} term - The term to search for and highlight.
 * @param {object} [options] - Additional options to customize the highlight behavior.
 * @param {string} [options.className="highlighted-text"] - CSS class applied to the highlighted term.
 * @returns {string | undefined} The updated text with highlighted term(s) wrapped in
 * `<span class="highlighted-text">...</span>`, or `undefined` if `term` is empty.
 */
export function highlight(text, term, {
  className = 'highlighted-text',
} = {}) {
  if (!text || !term) {
    return undefined;
  }

  const pattern = new RegExp(`(${term})`, 'gi');
  return text?.replace(
    pattern,
    `<span class="${className}">$1</span>`,
  );
}
